import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/platformConfig';
import { 
  XMarkIcon, 
  DocumentIcon, 
  TrashIcon,
  CloudArrowUpIcon,
  EyeIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { getAuth } from 'firebase/auth';
import DocumentDataExtraction from './DocumentDataExtraction';

interface Document {
  documentId: string;
  applicationId: string;
  documentType: string;
  fileName: string;
  fileUrl: string;
  uploadedAt: string;
  uploadedBy: string;
  status: 'pending' | 'received' | 'verified';
  notes?: string;
}

interface ApplicationDocumentsProps {
  applicationId: string;
  studentName: string;
  onClose: () => void;
}

const DOCUMENT_TYPES = [
  { type: 'birth_certificate', label: 'Birth Certificate' },
  { type: 'aadhar_card', label: 'Aadhar Card' },
  { type: 'transfer_certificate', label: 'Transfer Certificate' },
  { type: 'previous_school_cert', label: 'Previous School Certificate' },
  { type: 'medical_report', label: 'Medical Report' },
  { type: 'passport', label: 'Passport' },
  { type: 'vaccination_cert', label: 'Vaccination Certificate' },
  { type: 'other', label: 'Other Documents' }
];

const ApplicationDocuments: React.FC<ApplicationDocumentsProps> = ({ 
  applicationId, 
  studentName,
  onClose 
}) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState('birth_certificate');
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  const [showExtraction, setShowExtraction] = useState(false);
  const [selectedDocForExtraction, setSelectedDocForExtraction] = useState<Document | null>(null);

  useEffect(() => {
    fetchDocuments();
  }, [applicationId]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const auth = getAuth();
      const token = await auth.currentUser?.getIdToken(true);
      
      const response = await fetch(`${API_BASE_URL}/applications/${applicationId}/documents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setDocuments(data.data);
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleFileUpload = async (file: File) => {
    try {
      setUploading(true);
      setError('');
      
      const auth = getAuth();
      const token = await auth.currentUser?.getIdToken(true);
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentType', selectedDocType);

      const response = await fetch(`${API_BASE_URL}/applications/${applicationId}/documents`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Upload failed');
      }

      setDocuments([...documents, data.data]);
      alert('Document uploaded successfully!');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    if (!window.confirm('Delete this document?')) return;

    try {
      const auth = getAuth();
      const token = await auth.currentUser?.getIdToken(true);
      
      const response = await fetch(`${API_BASE_URL}/applications/${applicationId}/documents/${documentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setDocuments(documents.filter(d => d.documentId !== documentId));
      }
    } catch (err) {
      console.error('Error deleting document:', err);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'verified': return 'bg-green-100 text-green-800';
      case 'received': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDocumentLabel = (type: string) => {
    return DOCUMENT_TYPES.find(d => d.type === type)?.label || type;
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-6 border w-full max-w-4xl shadow-lg rounded-md bg-white mb-10">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Document Management</h2>
            <p className="text-gray-600">{studentName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Completion Progress */}
        <div className="mb-6 p-4 bg-blue-50 rounded-lg">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">Documents Uploaded</span>
            <span className="text-sm font-bold text-blue-600">{documents.length}</span>
          </div>
          <p className="text-xs text-gray-600">All documents are optional. Upload as needed.</p>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Upload Section */}
          <div className="lg:col-span-1">
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Upload Document</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Document Type
                </label>
                <select
                  value={selectedDocType}
                  onChange={(e) => setSelectedDocType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  {DOCUMENT_TYPES.map(doc => (
                    <option key={doc.type} value={doc.type}>
                      {doc.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Drag and Drop Area */}
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                  dragActive 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
                }`}
              >
                <CloudArrowUpIcon className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-600 mb-1">Drag and drop your file here</p>
                <p className="text-xs text-gray-500 mb-3">or</p>
                <label className="inline-block">
                  <input
                    type="file"
                    onChange={handleFileChange}
                    disabled={uploading}
                    className="hidden"
                  />
                  <span className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 cursor-pointer inline-block">
                    {uploading ? 'Uploading...' : 'Browse Files'}
                  </span>
                </label>
                <p className="text-xs text-gray-500 mt-2">Max 10MB</p>
              </div>
            </div>
          </div>

          {/* Documents List */}
          <div className="lg:col-span-2">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Uploaded Documents</h3>
            
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading documents...</div>
            ) : documents.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <DocumentIcon className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                <p>No documents uploaded yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {documents.map(doc => (
                  <div key={doc.documentId} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        <DocumentIcon className="h-5 w-5 text-gray-400 mt-1 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{doc.fileName}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {getDocumentLabel(doc.documentType)}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            Uploaded {new Date(doc.uploadedAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(doc.status)}`}>
                          {doc.status}
                        </span>
                        <button
                          onClick={() => {
                            setSelectedDocForExtraction(doc);
                            setShowExtraction(true);
                          }}
                          className="text-purple-600 hover:text-purple-900"
                          title="Extract Data"
                        >
                          <SparklesIcon className="h-4 w-4" />
                        </button>
                        <a
                          href={doc.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-900"
                          title="View Document"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </a>
                        <button
                          onClick={() => handleDeleteDocument(doc.documentId)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Document"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    {doc.notes && (
                      <p className="text-xs text-gray-600 mt-2 pl-8">Note: {doc.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Required Documents Checklist */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Tips</h3>
          <ul className="text-sm text-gray-600 space-y-2">
            <li>• Upload documents as parents provide them</li>
            <li>• All documents are optional</li>
            <li>• You can add notes to each document</li>
            <li>• Mark documents as verified once reviewed</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 pt-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium text-sm"
          >
            ✓ Done - Save Documents
          </button>
        </div>

        {/* Data Extraction Modal */}
        {showExtraction && selectedDocForExtraction && (
          <DocumentDataExtraction
            applicationId={applicationId}
            documentId={selectedDocForExtraction.documentId}
            documentType={selectedDocForExtraction.documentType}
            fileUrl={selectedDocForExtraction.fileUrl}
            onClose={() => {
              setShowExtraction(false);
              setSelectedDocForExtraction(null);
            }}
            onDataApplied={() => {
              // Refresh documents list
              fetchDocuments();
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ApplicationDocuments;
